"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.enableDefaultLogging = exports.disableDefaultLogging = exports.getConfiguredLogLevel = exports.setLogLevel = exports.removeLogListener = exports.addLogListener = exports.getScopedLogger = exports.error = exports.warn = exports.info = exports.debug = exports.verbose = exports.includeStackTraceFor = exports.shouldNotLog = exports.LogLevel = void 0;
const localEvent_1 = require("./localEvent");
const types_1 = require("./types");
Object.defineProperty(exports, "LogLevel", { enumerable: true, get: function () { return types_1.LogLevel; } });
const config_1 = require("./config");
const namespaces_1 = require("./namespaces");
const DEFAULT_LOG_LEVEL = types_1.LogLevel.Warn;
const DEFAULT_LOG_SOURCE = 'built';
const _logListener = new localEvent_1.LocalEvent(`${DEFAULT_LOG_SOURCE}-loglistener`);
const LogLevelValue = {
    [types_1.LogLevel.Verbose]: 4,
    [types_1.LogLevel.Debug]: 3,
    [types_1.LogLevel.Info]: 2,
    [types_1.LogLevel.Warn]: 1,
    [types_1.LogLevel.Error]: 0,
};
const getLogLevel = (logLevel, defaultLevel = DEFAULT_LOG_LEVEL) => {
    let level = LogLevelValue[logLevel];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (level === undefined) {
        // in case someone does something not type safe
        (0, exports.warn)(`Given log level '${logLevel}' is not a supported level. Defaulting to ${defaultLevel}`);
        return getLogLevel(defaultLevel);
    }
    return { name: logLevel, value: level };
};
let configuredLogLevel = getLogLevel(DEFAULT_LOG_LEVEL);
/**
 * Gets if given log level should be logged based on currently configured log level.
 *
 * @param logLevel - log level to check
 * @returns true if should NOT log; false if OK to log
 */
// should NOT log if the given level is higher (less severe) than configured
const shouldNotLog = (logLevel) => getLogLevel(logLevel, types_1.LogLevel.Info).value > configuredLogLevel.value;
exports.shouldNotLog = shouldNotLog;
let includeStackTraceLevels = [types_1.LogLevel.Debug];
/**
 * Use to override the default log levels to include stack trace in output.
 *
 * Note: Browser will include by default for warn and error.
 */
const includeStackTraceFor = (levels) => {
    includeStackTraceLevels = levels;
};
exports.includeStackTraceFor = includeStackTraceFor;
const defaultListener = (meta, ...logValues) => {
    var _a;
    if ((0, exports.shouldNotLog)(meta.level)) {
        return;
    }
    // eslint-disable-next-line no-console
    const logFunc = (_a = console[meta.level]) !== null && _a !== void 0 ? _a : console.log; // handle if log level func doesn't exist on browser
    if (includeStackTraceLevels.includes(meta.level)) {
        // we'll include stack trace for debugs to help trace source
        try {
            // just to be on the safe side..
            // we assert stack cuz it's prolly gonna be there, but we catch if not (and it's a pain to cover it missing in test)
            const stackTrace = new Error().stack.split('\n').slice(1).join('\n'); // omit the "Error" first line
            stackTrace && logValues.push('\n' + stackTrace);
        }
        catch (_b) { }
    }
    logFunc(`[${meta.source}]`, ...logValues);
};
/** Log to the verbose log event using default log source scope prefix. Use getScopedLogger to log with custom source. */
const verbose = (...args) => _logListener.notify({ level: types_1.LogLevel.Verbose, source: DEFAULT_LOG_SOURCE }, ...args);
exports.verbose = verbose;
/** Log to the debug log event using default log source scope prefix. Use getScopedLogger to log with custom source. */
const debug = (...args) => _logListener.notify({ level: types_1.LogLevel.Debug, source: DEFAULT_LOG_SOURCE }, ...args);
exports.debug = debug;
/** Log to the info log event using default log source scope prefix. Use getScopedLogger to log with custom source. */
const info = (...args) => _logListener.notify({ level: types_1.LogLevel.Info, source: DEFAULT_LOG_SOURCE }, ...args);
exports.info = info;
/** Log to the warn log event using default log source scope prefix. Use getScopedLogger to log with custom source. */
const warn = (...args) => _logListener.notify({ level: types_1.LogLevel.Warn, source: DEFAULT_LOG_SOURCE }, ...args);
exports.warn = warn;
/**
 * Log to the error log event using default log source scope prefix.
 *
 * Use getScopedLogger to log with custom source.
 *
 * **CONSIDER**: `trackError` in `monitoring-utility` may be a better option
 *   because it takes advantage of enhanced/multicast error tracking.
 *
 * */
const error = (...args) => _logListener.notify({ level: types_1.LogLevel.Error, source: DEFAULT_LOG_SOURCE }, ...args);
exports.error = error;
/**
 * Get level-based log functions that will prepend the given source scope.
 *
 * Note: These still call the configured log functions (default or customized via customizeLogFunctions).
 *
 * @param sourceScope - any string identifier to use a log source (prepends to calls to the returned log functions)
 */
const getScopedLogger = (sourceScope) => ({
    verbose: (...args) => _logListener.notify({ level: types_1.LogLevel.Verbose, source: sourceScope }, ...args),
    debug: (...args) => _logListener.notify({ level: types_1.LogLevel.Debug, source: sourceScope }, ...args),
    info: (...args) => _logListener.notify({ level: types_1.LogLevel.Info, source: sourceScope }, ...args),
    warn: (...args) => _logListener.notify({ level: types_1.LogLevel.Warn, source: sourceScope }, ...args),
    error: (...args) => _logListener.notify({ level: types_1.LogLevel.Error, source: sourceScope }, ...args),
});
exports.getScopedLogger = getScopedLogger;
/**
 * Add a listener for logging events.
 *
 * @param listener - custom log listener
 */
const addLogListener = (listener) => _logListener.subscribe(listener);
exports.addLogListener = addLogListener;
/**
 * Remove listener that was previously subscribed.
 *
 * @param listener - listener to remove
 */
const removeLogListener = (listener) => _logListener.unsubscribe(listener);
exports.removeLogListener = removeLogListener;
/**
 * Change minimum severity log level for logs in this library.
 *
 * Note: If logging still not happening, try calling enableDefaultLogging to ensure default listener is subscribed.
 *
 * @param logLevel - minimum log level
 */
const setLogLevel = (logLevel, ensureDefaultLogging) => {
    configuredLogLevel = getLogLevel(logLevel, configuredLogLevel.name);
    (0, config_1.saveConfigSection)('logging', { level: configuredLogLevel.name }); // persist across sessions
};
exports.setLogLevel = setLogLevel;
/** Get currently-configured log level info. */
const getConfiguredLogLevel = () => (Object.assign({}, configuredLogLevel));
exports.getConfiguredLogLevel = getConfiguredLogLevel;
/** Disables default logging (to console). */
const disableDefaultLogging = () => {
    _logListener.unsubscribe(defaultListener);
};
exports.disableDefaultLogging = disableDefaultLogging;
/** Ensures default logging (to console) is enabled. */
const enableDefaultLogging = () => {
    _logListener.subscribe(defaultListener);
};
exports.enableDefaultLogging = enableDefaultLogging;
// load from config if it's set
const savedConfig = (0, config_1.getConfig)();
if ((_a = savedConfig.logging) === null || _a === void 0 ? void 0 : _a.level) {
    const level = getLogLevel(savedConfig.logging.level);
    (0, exports.setLogLevel)(level.name);
}
(0, exports.enableDefaultLogging)();
// expose setting in console via window:
const LOGGING_NS = 'Logging';
(0, namespaces_1.addToBuiltNamespace)(LOGGING_NS, 'setLogLevel', exports.setLogLevel);
(0, namespaces_1.addToBuiltNamespace)(LOGGING_NS, 'getLogLevel', exports.getConfiguredLogLevel);
